import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import frToEn from "./fr-en.json";
import manualFrToEn from "./manual-fr-en";

const STORAGE_KEY = "wonyasoft-language";
const TEXT_ATTRIBUTES = ["placeholder", "title", "aria-label", "alt"];
const BLOCKED_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "TEXTAREA",
  "PRE",
  "CODE",
]);
const LanguageContext = createContext({
  language: "fr",
  setLanguage: () => {},
  toggleLanguage: () => {},
  t: (value) => value,
});

const originalTextNodes = new WeakMap();
const originalAttributes = new WeakMap();
const translations = { ...frToEn, ...manualFrToEn };

function normalizeText(value) {
  return value.replace(/\s+/g, " ").trim();
}

function preserveWhitespace(original, translated) {
  const leading = original.match(/^\s*/)?.[0] ?? "";
  const trailing = original.match(/\s*$/)?.[0] ?? "";
  return `${leading}${translated}${trailing}`;
}

function isIgnoredElement(element) {
  if (!element) {
    return false;
  }

  if (element.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  if (element.getAttribute("data-no-i18n") === "true") {
    return true;
  }

  if (BLOCKED_TAGS.has(element.tagName)) {
    return true;
  }

  if (element.classList?.contains("material-icons")) {
    return true;
  }

  return false;
}

function hasIgnoredAncestor(node) {
  let current = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;

  while (current) {
    if (isIgnoredElement(current)) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
}

function readOriginalText(node) {
  if (!originalTextNodes.has(node)) {
    originalTextNodes.set(node, node.textContent ?? "");
  }

  return originalTextNodes.get(node) ?? "";
}

function readOriginalAttribute(element, attribute) {
  if (!originalAttributes.has(element)) {
    originalAttributes.set(element, new Map());
  }

  const attributes = originalAttributes.get(element);
  if (!attributes.has(attribute)) {
    attributes.set(attribute, element.getAttribute(attribute) ?? "");
  }

  return attributes.get(attribute) ?? "";
}

function translateTextNode(node, language) {
  const original = readOriginalText(node);

  if (language === "fr") {
    if (node.textContent !== original) {
      node.textContent = original;
    }
    return;
  }

  const normalized = normalizeText(original);
  if (!normalized) {
    return;
  }

  const translation = translations[normalized];
  if (!translation) {
    return;
  }

  const translatedValue = preserveWhitespace(original, translation);
  if (node.textContent !== translatedValue) {
    node.textContent = translatedValue;
  }
}

function translateElementAttributes(element, language) {
  for (const attribute of TEXT_ATTRIBUTES) {
    if (!element.hasAttribute(attribute)) {
      continue;
    }

    const original = readOriginalAttribute(element, attribute);

    if (language === "fr") {
      if (element.getAttribute(attribute) !== original) {
        element.setAttribute(attribute, original);
      }
      continue;
    }

    const normalized = normalizeText(original);
    if (!normalized) {
      continue;
    }

    const translation = translations[normalized];
    if (translation && element.getAttribute(attribute) !== translation) {
      element.setAttribute(attribute, translation);
    }
  }
}

function translateTree(root, language) {
  if (!root || hasIgnoredAncestor(root)) {
    return;
  }

  const elements = [root, ...root.querySelectorAll("*")];

  for (const element of elements) {
    if (
      isIgnoredElement(element) ||
      hasIgnoredAncestor(element.parentElement)
    ) {
      continue;
    }
    translateElementAttributes(element, language);
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!normalizeText(node.textContent ?? "")) {
        return NodeFilter.FILTER_REJECT;
      }

      if (hasIgnoredAncestor(node)) {
        return NodeFilter.FILTER_REJECT;
      }

      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let current = walker.nextNode();
  while (current) {
    translateTextNode(current, language);
    current = walker.nextNode();
  }
}

export function LanguageProvider({ children }) {
  const location = useLocation();
  const [language, setLanguage] = useState(() => {
    if (typeof window === "undefined") {
      return "fr";
    }

    const storedLanguage = window.localStorage.getItem(STORAGE_KEY);
    return storedLanguage === "en" ? "en" : "fr";
  });
  const isApplyingRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.setAttribute("data-language", language);
  }, [language]);

  useEffect(() => {
    const root =
      document.querySelector("main") || document.getElementById("root");

    if (!root) {
      return undefined;
    }

    let animationFrame = 0;
    let timeoutId = 0;

    const applyTranslations = () => {
      if (isApplyingRef.current) {
        return;
      }

      isApplyingRef.current = true;
      try {
        translateTree(root, language);
      } finally {
        isApplyingRef.current = false;
      }
    };

    const scheduleTranslations = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(applyTranslations);
    };

    scheduleTranslations();
    timeoutId = window.setTimeout(scheduleTranslations, 150);

    const observer = new MutationObserver(() => {
      if (!isApplyingRef.current) {
        scheduleTranslations();
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: TEXT_ATTRIBUTES,
    });

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(timeoutId);
    };
  }, [language, location.pathname]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        toggleLanguage: () =>
          setLanguage((currentLanguage) =>
            currentLanguage === "fr" ? "en" : "fr",
          ),
        t: (value) =>
          language === "en" ? (translations[value] ?? value) : value,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
